import { TimeZoneType } from "../../constants/timezones"
import { HolidayViewModel } from "../../holidays/api/holiday"
import { GroupBy } from "../../types/common"
import { TreeGridTranslations } from "../hooks/use-translations"
import { Option } from "../types"
import { makeColorKeysEnum, makeGroupByEnum, mapGroupByToTranslations, mapGroupByToColumns } from "../utils"
import { makeColumns } from "./columns"
import { ProjectColumnName } from "./types"
import { makeCfgBase } from "../base/layout/make-cfg-base"
import { makeColumnLayout } from "../base/layout/make-column-layout"
import { ToolbarBuilder } from "../base/layout/make-toolbar"
import { ProjectsViewOptions } from "../../projects/components/ProjectsViewSelect"

export const makeTreegridLayout = (props: CreateTreegridLayoutProps) => {
  const Cfg = makeCfg(props)
  const Cols = makeColumns(props)
  const Head = makeHead(props)
  const Header = makeHeader({ columns: Cols, ...props })
  const Lang = makeLang(props)
  const Toolbar = makeToolbar(props)
  const Actions = makeActions()
  const Solid = [{ id: "NoData", Html: props.translations.noData }]
  const columnLayout = makeColumnLayout(Cols)

  return {
    Cfg,
    ...columnLayout,
    Head,
    Header,
    Lang,
    Toolbar,
    Actions,
    Solid,
    Def: makeDef({ showGantt: props.showGantt }),
  }
}

const makeCfg = ({
  id,
  groupBy,
  projectsView,
}: {
  id: string
  groupBy: GroupBy
  projectsView: ProjectsViewOptions
}) => {
  const baseCfg = makeCfgBase({ id, cfgId: makeViewSpecificCfgId(id, projectsView) })
  /** @link link for documentation: https://www.treegrid.com/Doc/SearchAttr.html */
  return {
    ...baseCfg,
    Sort: "plannedEndDate, plannedStartDate",
    Group: mapGroupByToColumns(groupBy),
    GroupMain: "title",
    SaveAttrs: "Toolbar,SelectGroupBy",
    Version: 6,
  }
}

const makeViewSpecificCfgId = (id: string, view: ProjectsViewOptions) => `${id}_${view}`

const makeDef = (props: { showGantt: boolean }) => {
  return [
    {
      Name: "Run",
      Def: "Group",
      Calculated: "1",
      RUNFormula: props.showGantt ? "ganttrunsum()" : "",
      Expanded: "1",
      Height: "48",
      titleClass: "group-header",
      Spanned: "1",
      titleSpan: "17",
      openSpan: "3",
    },
  ]
}

const makeHead = ({ dateFormat, gridInfo, showFilters, translations }: MakeHeadProps) => {
  const headerRow = {
    id: "headerRow",
    Name: translations.pageHeaderRowLabel,
    Space: -1,
    Visible: 0,
    PanelVisible: 0,
    CanExport: 0,
    CanPrint: 2,
    Cells: `Logo,GridInfo,PrintDate`,
    LogoRelWidth: 1,
    LogoAligh: "Left",
    LogoVAligh: "middle",
    LogoType: "Html",
    Logo: `<img style="height: 40px;" src="/favicon.png"  alt="company logo"/>`,
    PrintDate: new Date(),
    PrintDateType: "Date",
    PrintDateFormat: dateFormat,
    PrintDateCanEdit: 0,
    GridInfo: `<span class="largeText">${gridInfo}<span>`,
    GridInfoType: "Html",
    GridInfoRelWidth: 2,
  }
  const filtersRow = {
    CanExport: 0,
    CanPrint: 0,
    id: "filterRow",
    Kind: "Filter",
    ganttBarColorRange: 1,
    ganttBarColorFilterEnumKeys: 1,
    ganttBarColorEnumKeys: makeColorKeysEnum(translations),
    statusFilterOff: translations.statusFilterOffLabel,
    managersFilterOff: translations.managersFilterOffLabel,
    participantsFilterOff: translations.participantsFilterOffLabel,
    suppliersFilterOff: translations.suppliersFilterOffLabel,
    workspacesFilterOff: translations.workspacesFilterOffLabel,
    stateFilterOff: translations.stateFilterOffLabel,
    customersFilterOff: translations.customersFilterOffLabel,
    Visible: showFilters ? 1 : 0,
  }

  return [headerRow, filtersRow]
}

const makeLang = ({ firstDayOfWeek, dateSeparator, translations }: MakeLangProps) => {
  return {
    Format: {
      FirstWeekDay: firstDayOfWeek,
      GMT: 1,
      DateSeparator: dateSeparator,
      LongDayNames: translations.longDayNames,
      ShortDayNames: translations.shortDayNames,
      Day2CharNames: translations.day2CharNames,
      Day1CharNames: translations.day1CharNames,
      LongMonthNames: translations.longMonthNames,
      LongMonthNames2: translations.longMonthNames,
      ShortMonthNames: translations.shortMonthNames,
    },
    MenuColumns: {
      Caption: translations.chooseItemsLabel,
      ColsCaption: translations.chooseColumnsLabel,
      SpaceCaption: translations.chooseToolbarsLabel,
      RowsCaption: translations.chooseRowsLabel,
    },
    MenuButtons: {
      Ok: translations.okButtonLabel,
      Cancel: translations.cancelButtonLabel,
      HideAll: translations.hideAllButtonLabel,
      All: translations.allOnButtonLabel,
      Clear: translations.clear,
    },
    MenuPrint: {
      Caption: translations.printOptionsLabel,
      PrintOptions: translations.printOptionsLabel,
      PrintRows: translations.printMaxRowsPerPageLabel,
      PrintPageRoot: translations.printStartRowLabel,
      PrintPageSize: translations.printPageSizeLabel,
      PrintPageOptions: translations.printBrowserPrintInfoLabel,
      PrintPageOrientation: translations.printPageOrientationLabel,
      PrintPageOrientations: `${translations.portraitLabel},${translations.landscapeLabel}`,
      PrintMarginWidth: translations.printMarginWidthLabel,
      PrintMarginHeight: translations.printMarginHeightLabel,
      PDFFitPage: translations.printFitToPageLabel,
      PDFFitPages: `${translations.noneLabel},${translations.widthLabel},${translations.heightLabel},${translations.singlePageLabel}`,
      PrintDPI: translations.printDPIRatioLabel,
      PrintWidth: translations.printPageWidthLabel,
      PrintHeight: translations.printPageHeightLabel,
      PrintResults: translations.printSizeInfo,
    },
    MenuExport: {
      Caption: translations.exportOptionsLabel,
      ExportOptions: translations.exportOptionsLabel,
      ExportName: translations.exportFileNameLabel,
      ExportFormat: translations.exportFileFormatLabel,
      ExportExpanded: translations.exportAllRowsExpandedLabel,
      ExportOutline: translations.exportTreeInExcelLabel,
      ExportIndent: translations.exportIndentTreeInMainColumnLabel,
      ExportFixedRows: translations.exportFreezeHeadRowsLabel,
      ExportFixedCols: translations.exportFreezeLeftColumnsLabel,
      ExportEmpty: translations.exportEmptyCellsLabel,
    },
    MenuFilter: {
      F0: translations.filterOffLabel,
      F1: translations.filterEqualLabel,
      F2: translations.filterNotEqualLabel,
      F3: translations.filterLessThanLabel,
      F4: translations.filterLessThanOrEqualLabel,
      F5: translations.filterGreaterThanLabel,
      F6: translations.filterGreaterThanOrEqualLabel,
      F7: translations.filterBeginsWithLabel,
      F8: translations.filterDoesnotBeginWithLabel,
      F9: translations.filterEndsWithLabel,
      F10: translations.filterDoesnotEndWithLabel,
      F11: translations.filterContainsLabel,
      F12: translations.filterDoesnotContainLabel,
    },
    Gantt: {
      ErrEnd: translations.startDateAfterEndDateError,
      ErrStart: translations.startDateAfterEndDateError,
    },
  }
}

const makeToolbar = ({
  canCreateProjects,
  showFilters,
  showGanttToolbarActions,
  groupBy,
  showGroupBy,
  translations,
}: MakeToolbarProps) => {
  /**
   * @link https://www.treegrid.com/Doc/GlobalSettings.htm#DefaultToolbar
   */
  const toolbarBuilder = new ToolbarBuilder()
    .addCustomToolbarConfig({
      Cells10Search: "AddProject,Search,Summary",
      AddProjectType: "Button",
      AddProjectButton: "Button",
      AddProject: canCreateProjects
        ? `<button>${translations.addProjectButtonLabel}<span class="icon"> + </span></button>`
        : 0,
      AddProjectClass: "toolbarCustomButton toolbarCustomButton--primary",
    })
    .addToolbarSearch(translations)
    .addToolbarSummary(translations.toolbarProjectListSummaryText)
    .addToolbarFilter(showFilters)
    .addToolbarGanttZoom({ showGanttToolbarActions, translations })
    .addToolbarTips(translations)

  if (showGroupBy) {
    toolbarBuilder.addCustomToolbarConfig({
      Cells90Right: "Spacer",
      Cells80Right: "SelectGroupBy",
      SelectGroupByType: "Enum",
      SelectGroupByEnum: makeGroupByEnum({ withResponsibles: false, translations }),
      SelectGroupBy: mapGroupByToTranslations({ withResponsibles: false, translations })[groupBy],
      SelectGroupByWidth: 200,
      SelectGroupByOnChange: "window.Grids.OnSelectGroupByChange(Grid, Value)",
    })
  }

  return toolbarBuilder.build()
}

const makeHeader = ({ columns, translations }: MakeHeaderProps) => {
  const headerColumns = [...columns.LeftCols, ...columns.Cols, ...columns.RightCols]
  const Header: Record<string, string | number> = {
    Name: translations.headerRowLabel,
    plannedStartDateClass: "text-right",
    plannedEndDateClass: "text-right",
    actualStartDateClass: "text-right",
    actualEndDateClass: "text-right",
    statusClass: "text-center",
    ganttBarColorClass: "text-center",
    Wrap: 1,
  }
  headerColumns.forEach((col) => {
    if (col.Name) {
      Header[col.Name] = col.header as string
      Header[`${col.Name}Wrap`] = 1
    }
  })
  return Header
}

const makeActions = () => {
  return {
    OnIns: "", // This is for resetting the default behavior of treegrid, which is to AddRow on insert key press
    OnDel: "", // This is for resetting the default behavior of treegrid, which is to DeleteRow on delete key press
  }
}

type MakeToolbarProps = {
  canCreateProjects: boolean
  showFilters: boolean
  showGanttToolbarActions: boolean
  groupBy: GroupBy
  showGroupBy: boolean
  translations: TreeGridTranslations
}

type MakeHeadProps = {
  showFilters: boolean
  dateFormat: string
  gridInfo: string
  translations: TreeGridTranslations
}

type CreateTreegridLayoutProps = {
  id: string
  dateFormat: string
  dateSeparator: string
  translations: TreeGridTranslations
  defaultColumnOrder: ProjectColumnName[]
  defaultVisibleColumns: ProjectColumnName[]
  canCreateProjects: boolean
  showFilters: boolean
  gridInfo: string
  options: Option[]
  firstDayOfWeek: DayOfWeek
  showGantt: boolean
  showGanttToolbarActions: boolean
  timeZone: TimeZoneType
  weekendDays: DayOfWeek[]
  groupBy: GroupBy
  showGroupBy: boolean
  holidays: HolidayViewModel[]
  ganttZoomLeftEdgeRefDate: Date
  ganttZoomRightEdgeRefDate: Date
  projectsView: ProjectsViewOptions
}

type MakeLangProps = {
  firstDayOfWeek: DayOfWeek
  dateSeparator: string
  translations: TreeGridTranslations
}

type MakeHeaderProps = {
  columns: MadeColumns
  translations: TreeGridTranslations
}

type HeaderColumn = { header?: string; Name?: string }

type MadeColumns = {
  LeftCols: HeaderColumn[]
  Cols: HeaderColumn[]
  RightCols: HeaderColumn[]
}
